import React, { useCallback, useEffect, useRef, useState } from "react";
import { Image as ImageIcon, Upload, Video, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { api, API, formatApiError } from "@/lib/api";

const DEFAULT_HERO =
  "https://images.unsplash.com/photo-1785149882009-40d00251045c?crop=entropy&cs=srgb&fm=jpg&q=80&w=1600";

export default function HeroImageManager() {
  const [settings, setSettings] = useState({
    hero_image: false,
    hero_image_updated_at: "",
    hero_video: false,
    hero_video_updated_at: "",
  });
  const [uploading, setUploading] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const fileRef = useRef(null);
  const videoRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get("/settings");
      setSettings(res.data);
    } catch (e) {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      await api.post("/admin/hero-image", fd);
      toast.success("Imagem do topo atualizada!");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const onVideo = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 30 * 1024 * 1024) {
      toast.error("Vídeo muito grande (máx. 30MB). Comprima ou use um clipe mais curto.");
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    setUploadingVideo(true);
    try {
      await api.post("/admin/hero-video", fd);
      toast.success("Vídeo do topo atualizado!");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setUploadingVideo(false);
    }
  };

  const removeVideo = async () => {
    try {
      await api.delete("/admin/hero-video");
      toast.success("Vídeo removido. Voltamos a usar a imagem.");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const preview = settings.hero_image
    ? `${API}/settings/hero-image?v=${encodeURIComponent(settings.hero_image_updated_at)}`
    : DEFAULT_HERO;
  const videoSrc = settings.hero_video
    ? `${API}/settings/hero-video?v=${encodeURIComponent(settings.hero_video_updated_at)}`
    : null;

  return (
    <div className="mt-8" data-testid="hero-image-manager">
      <div className="flex items-center gap-2">
        <ImageIcon className="h-5 w-5 text-[#1B7A45]" />
        <h2 className="font-display text-xl font-bold tracking-tight text-slate-900">
          Topo do site (atrás da logo)
        </h2>
      </div>
      <p className="mt-1 text-sm text-slate-500">
        Escolha uma <strong>imagem</strong> ou um <strong>vídeo</strong> de fundo. Se houver vídeo,
        ele é exibido no lugar da imagem.
      </p>

      <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
        {/* Imagem */}
        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <div className="relative overflow-hidden rounded-xl">
            <img src={preview} alt="Prévia do topo" className="h-40 w-full object-cover" />
            <div className="absolute inset-0 bg-[#0b1610]/70" />
            <span className="absolute bottom-2 left-3 text-xs font-medium text-white/90">
              {settings.hero_image ? "Imagem personalizada" : "Imagem padrão"}
            </span>
          </div>
          <input
            ref={fileRef}
            type="file"
            accept="image/png,image/jpeg,image/webp,image/gif"
            onChange={onFile}
            className="hidden"
            data-testid="hero-file-input"
          />
          <Button
            data-testid="hero-upload-btn"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="mt-3 w-full rounded-xl bg-[#1B7A45] py-5 font-semibold text-white hover:bg-[#156337] disabled:opacity-50"
          >
            <Upload className="mr-2 h-4 w-4" />
            {uploading ? "Enviando..." : "Trocar imagem"}
          </Button>
        </div>

        {/* Vídeo */}
        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <div className="relative overflow-hidden rounded-xl bg-slate-900">
            {videoSrc ? (
              <video
                src={videoSrc}
                muted
                loop
                autoPlay
                playsInline
                className="h-40 w-full object-cover"
              />
            ) : (
              <div className="flex h-40 w-full items-center justify-center text-slate-500">
                <Video className="h-8 w-8" />
              </div>
            )}
            <span className="absolute bottom-2 left-3 text-xs font-medium text-white/90">
              {settings.hero_video ? "Vídeo ativo" : "Sem vídeo"}
            </span>
          </div>
          <input
            ref={videoRef}
            type="file"
            accept="video/mp4,video/webm,video/quicktime"
            onChange={onVideo}
            className="hidden"
            data-testid="hero-video-input"
          />
          <div className="mt-3 flex gap-2">
            <Button
              data-testid="hero-video-upload-btn"
              onClick={() => videoRef.current?.click()}
              disabled={uploadingVideo}
              className="flex-1 rounded-xl bg-[#E8611A] py-5 font-semibold text-white hover:bg-[#CF5212] disabled:opacity-50"
            >
              <Video className="mr-2 h-4 w-4" />
              {uploadingVideo ? "Enviando..." : settings.hero_video ? "Trocar vídeo" : "Enviar vídeo"}
            </Button>
            {settings.hero_video && (
              <Button
                data-testid="hero-video-remove-btn"
                onClick={removeVideo}
                variant="outline"
                className="rounded-xl border-red-200 bg-red-50 py-5 text-red-600 hover:bg-red-100"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
          <p className="mt-2 text-xs text-slate-400">MP4/WEBM/MOV · máx. 30MB · sem áudio, em loop.</p>
        </div>
      </div>
    </div>
  );
}
